import * as React from 'react';

import { cn } from '@/utils/utils';
import { ScrollAreaRoot, ScrollAreaViewport, ScrollBar } from './scroll-area';

const Table = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableElement>) => {
  const viewport = React.useRef<HTMLDivElement>(null);
  const root = React.useRef<HTMLDivElement>(null);
  const [isOnScreen, setIsOnScreen] = React.useState(false);
  const [useFixedBar, setUseFixedBar] = React.useState(false);
  const [hasScroll, setHasScroll] = React.useState(false);
  const prevUseFixedRef = React.useRef<boolean>(false);
  // rAF-batched scroll + cached dimensions
  const rafIdRef = React.useRef<number | null>(null);
  const scheduledRef = React.useRef(false);
  const lastVarsRef = React.useRef({ left: -1, right: -1, widthDelta: -1 });
  const dimsRef = React.useRef({ clientWidth: 0, scrollWidth: 0 });
  React.useEffect(() => {
    if (!viewport.current) return;
    // Override some inline styles Radix may set that break our positioning
    const scrollbar = root.current?.querySelector(
      '[data-slot="scroll-area-scrollbar"]'
    ) as HTMLDivElement | null;
    if (scrollbar?.style.left) {
      scrollbar?.style.setProperty('left', null);
      scrollbar?.style.setProperty('position', null);
    }
    const scroller = viewport.current;

    // rAF-batched application of scroll CSS vars
    const applyScrollVars = () => {
      scheduledRef.current = false;
      // If a fallback timer was set, clear it now that rAF ran
      if (fallbackTimerRef.current != null) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      if (!scroller) return;
      const { clientWidth, scrollWidth } = dimsRef.current;
      const scrollLeft = scroller.scrollLeft;
      const scrollRight = Math.max(scrollWidth - clientWidth - scrollLeft, 0);
      if (scrollLeft !== lastVarsRef.current.left) {
        scroller.style.setProperty('--scrollLeft', String(scrollLeft));
        lastVarsRef.current.left = scrollLeft;
      }
      if (scrollRight !== lastVarsRef.current.right) {
        scroller.style.setProperty('--scrollRight', String(scrollRight));
        lastVarsRef.current.right = scrollRight;
      }
    };
    const fallbackTimerRef = {
      current: null as number | null,
    } as React.RefObject<number | null>;
    const scheduleApply = () => {
      if (scheduledRef.current) return;
      scheduledRef.current = true;
      rafIdRef.current = window.requestAnimationFrame(applyScrollVars);
      // Fallback for browsers that throttle rAF during scroll (e.g., iOS Safari)
      // If rAF hasn't fired within ~120ms, apply immediately to keep vars fresh
      fallbackTimerRef.current = window.setTimeout(() => {
        if (scheduledRef.current) {
          applyScrollVars();
        }
      }, 120);
    };

    const onScroll: EventListener = () => {
      scheduleApply();
    };
    // Initial schedule
    scheduleApply();
    if (!scroller) return;
    scroller.addEventListener('scroll', onScroll, { passive: true });

    // Keep dimensions cached and update --scrollWidth on size changes
    const resizeObserver = new ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        for (let entry of entries) {
          if (entry.target === scroller) {
            const cw = scroller.clientWidth;
            const sw = scroller.scrollWidth;
            dimsRef.current.clientWidth = cw;
            dimsRef.current.scrollWidth = sw;
            const widthDelta = Math.max(sw - cw, 0);
            if (widthDelta !== lastVarsRef.current.widthDelta) {
              scroller.style.setProperty('--scrollWidth', String(widthDelta));
              lastVarsRef.current.widthDelta = widthDelta;
              // Track if there's scrollable content for showing/hiding scrollbar
              setHasScroll(widthDelta > 0);
            }
            scheduleApply();
          }
        }
      }
    );
    resizeObserver.observe(scroller);

    // Helper to update bar alignment and fixed/anchored mode
    const updateFromRect = (
      rect: DOMRectReadOnly,
      rootBounds: DOMRectReadOnly | null
    ) => {
      const viewportLeft = rootBounds?.left ?? 0;
      const viewportRight = rootBounds?.right ?? window.innerWidth;
      const viewportBottom = rootBounds?.bottom ?? window.innerHeight;
      const left = Math.max(rect.left, viewportLeft);
      const right = Math.min(rect.right, viewportRight);
      const width = Math.max(right - left, 0);
      root.current?.style.setProperty('--sa-left', `${left}px`);
      root.current?.style.setProperty('--sa-width', `${width}px`);
      // Hysteresis around viewport bottom to avoid flapping
      const H = 2; // px
      const currentlyFixed = prevUseFixedRef.current;
      const crossesBottom = rect.top < viewportBottom;
      const shouldFix = currentlyFixed
        ? crossesBottom && rect.bottom > viewportBottom - H
        : crossesBottom && rect.bottom > viewportBottom + H;
      if (prevUseFixedRef.current !== shouldFix) {
        prevUseFixedRef.current = shouldFix;
        setUseFixedBar(shouldFix);
      }
    };

    // Track visibility and update alignment on page scroll
    const thresholds = Array.from({ length: 11 }, (_, i) => i / 10);
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsOnScreen(entry.isIntersecting);
        updateFromRect(entry.boundingClientRect, entry.rootBounds as any);
      },
      { root: null, threshold: thresholds }
    );
    io.observe(scroller);

    // Window resize: update alignment and dimensions if needed
    const onWinResize = () => {
      updateFromRect(scroller.getBoundingClientRect(), null);
      const cw = scroller.clientWidth;
      const sw = scroller.scrollWidth;
      if (
        cw !== dimsRef.current.clientWidth ||
        sw !== dimsRef.current.scrollWidth
      ) {
        dimsRef.current.clientWidth = cw;
        dimsRef.current.scrollWidth = sw;
        const widthDelta = Math.max(sw - cw, 0);
        if (widthDelta !== lastVarsRef.current.widthDelta) {
          scroller.style.setProperty('--scrollWidth', String(widthDelta));
          lastVarsRef.current.widthDelta = widthDelta;
          // Track if there's scrollable content for showing/hiding scrollbar
          setHasScroll(widthDelta > 0);
        }
        scheduleApply();
      }
    };
    window.addEventListener('resize', onWinResize);

    // Initial dimension cache and alignment
    dimsRef.current.clientWidth = scroller.clientWidth;
    dimsRef.current.scrollWidth = scroller.scrollWidth;
    const initialDelta = Math.max(
      scroller.scrollWidth - scroller.clientWidth,
      0
    );
    scroller.style.setProperty('--scrollWidth', String(initialDelta));
    // Track if there's scrollable content for showing/hiding scrollbar
    setHasScroll(initialDelta > 0);
    lastVarsRef.current.widthDelta = initialDelta;
    updateFromRect(scroller.getBoundingClientRect(), null);

    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      scroller.removeEventListener('scroll', onScroll as any);
      resizeObserver.unobserve(scroller);
      io.unobserve(scroller);
      window.removeEventListener('resize', onWinResize);
    };
  }, []);
  return (
    <div className="relative">
      <ScrollAreaRoot ref={root} type="always">
        <ScrollAreaViewport
          ref={viewport}
          data-slot="table-container"
          className={cn(
            'w-full overflow-auto',
            // Only add bottom padding when there's scrollable content to accommodate the scrollbar
            hasScroll && 'pb-2.5',
            "after:rounded after:shadow-[inset_-15px_0px_15px_-15px_rgba(0,0,0,calc(0.75*(clamp(0,var(--scrollRight),100)/100)))] after:content-['_'] after:absolute after:h-[calc(100%+1px)] after:w-4 after:top-0 after:right-0"
          )}
        >
          <table
            data-slot="table"
            className={cn('w-full caption-bottom text-sm', className)}
            {...props}
          />
        </ScrollAreaViewport>
        <ScrollBar
          orientation="horizontal"
          className={cn(
            'bottom-0 bg-background/80 supports-[backdrop-filter]:bg-background/60 backdrop-blur',
            '[&_[data-slot="scroll-area-thumb"]]:bg-accent-foreground/70!',
            // Only show scrollbar when content is scrollable (hasScroll tracks --scrollWidth > 0)
            !hasScroll && 'hidden',
            // Two modes: fixed to viewport bottom vs anchored to table bottom
            useFixedBar
              ? 'fixed z-40 left-[var(--sa-left)] w-[var(--sa-width)]'
              : 'absolute z-20 left-0 right-0',
            // Only interact/show when the table is on screen
            !isOnScreen && 'pointer-events-none opacity-0'
          )}
          aria-hidden={!isOnScreen}
        />
      </ScrollAreaRoot>
    </div>
  );
};
Table.displayName = 'Table';

const TableHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <thead
    data-slot="table-header"
    className={cn('[&_tr]:border-b', className)}
    {...props}
  />
);
TableHeader.displayName = 'TableHeader';

const TableBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody
    data-slot="table-body"
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
);
TableBody.displayName = 'TableBody';

const TableFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tfoot
    data-slot="table-footer"
    className={cn('bg-muted font-medium text-dark border-t', className)}
    {...props}
  />
);
TableFooter.displayName = 'TableFooter';

const TableRow = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr
    data-slot="table-row"
    className={cn(
      'group border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      className
    )}
    {...props}
  />
);
TableRow.displayName = 'TableRow';

const TableHead = ({
  className,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement>) => (
  <th
    data-slot="table-head"
    className={cn(
      'h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
);
TableHead.displayName = 'TableHead';

const TableCell = ({
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) => (
  <td
    data-slot="table-cell"
    className={cn(
      'py-2 px-4 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
);
TableCell.displayName = 'TableCell';

const TableCaption = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableCaptionElement>) => (
  <caption
    data-slot="table-caption"
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
);
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
